/**
 * Embedding similarity — read path for the "Recommended for you" nudge (It-7).
 *
 * The vectors themselves never leave Postgres: `similar_venues` (schema §30c)
 * takes the venues the diner has actually visited and returns
 * (seed → neighbour, cosine similarity) triples — ids and a number, nothing
 * else. Six kilobytes of floats per venue would be a silly thing to ship to a
 * phone, and the maths belongs where the data is.
 *
 * Everything here is fail-soft, like `visits.ts` and `analytics.ts`: any
 * failure returns null and the recommendation engine simply runs without the
 * signal. The RPC is granted to `authenticated` only, and the strip it feeds is
 * signed-in only, so guests never reach this file.
 *
 * Pre-migration safety: the app deploys before Teo pastes §30, and the flag can
 * be flipped on before the paste. A missing function is therefore an expected
 * state, not an error — it's recorded once and the endpoint is written off for
 * the session (same idiom as the §28 column fail-soft in data/restaurants/columns.ts).
 */
import { supabase } from "@/lib/supabase";
import { isMissingFunction } from "@/lib/rpc";
import type { RestaurantId } from "@/types/restaurant";

/** seed venue id → (neighbour venue id → cosine similarity, 0..1). */
export type SimilarityIndex = Map<string, Map<string, number>>;

/** §30c slices the input array at 25 — mirror it so we never send dead weight. */
const MAX_SEEDS = 25;
/** Neighbours per seed. The RPC clamps to 20; 5 is plenty for a nudge. */
const NEIGHBORS_PER_SEED = 5;
/**
 * The strip waits for this answer before it paints (so it never reshuffles
 * under the visitor), which makes a hung request a hung strip. Give up early
 * and render without the nudge — it is a garnish, not the dish.
 */
const TIMEOUT_MS = 6000;

/**
 * Sticky per-session: set when the DB has no `similar_venues` (schema §30 not
 * pasted yet). Cleared only by a reload — the migration is a manual step, so
 * re-asking on every render would just be noise.
 */
let rpcMissing = false;

interface SimilarRow {
  source_id: string;
  venue_id: string;
  similarity: number;
}

/**
 * Neighbours for every venue in `venueIds`, in one round trip. Returns null
 * when the signal is unavailable for ANY reason (no ids, migration pending,
 * network hiccup, malformed reply) — callers treat null as "run without it".
 */
export async function fetchSimilarityIndex(
  venueIds: readonly RestaurantId[],
): Promise<SimilarityIndex | null> {
  if (rpcMissing) return null;
  const seeds = Array.from(new Set(venueIds)).slice(0, MAX_SEEDS);
  if (seeds.length === 0) return null;

  try {
    const { data, error } = await Promise.race([
      supabase.rpc("similar_venues", { p_venue_ids: seeds, p_limit: NEIGHBORS_PER_SEED }),
      new Promise<{ data: null; error: null }>((res) =>
        setTimeout(() => res({ data: null, error: null }), TIMEOUT_MS),
      ),
    ]);
    if (error) {
      if (isMissingFunction(error)) rpcMissing = true;
      return null;
    }
    if (!Array.isArray(data)) return null;

    const index: SimilarityIndex = new Map();
    for (const row of data as SimilarRow[]) {
      const source = row?.source_id;
      const target = row?.venue_id;
      const raw = Number(row?.similarity);
      // Cosine over unit vectors lives in [-1, 1]; a negative or non-finite
      // score is not a recommendation, so it's dropped rather than clamped
      // into a weak positive.
      if (typeof source !== "string" || typeof target !== "string") continue;
      if (!Number.isFinite(raw) || raw <= 0 || source === target) continue;
      const bucket = index.get(source) ?? new Map<string, number>();
      bucket.set(target, Math.min(1, raw));
      index.set(source, bucket);
    }
    return index.size > 0 ? index : null;
  } catch {
    return null;
  }
}
